import * as React from 'react';
import { IMaskInput } from 'react-imask';

import { Icon } from '../icon/icon.tsx';
import { Hint, type HintContent } from '../hint/hint.tsx';

import clsx from 'clsx';
import './input.css';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  /** Content behind the "?" beside the label; without it there is no "?". */
  hint?: HintContent;
  required?: boolean;
  state?: 'default' | 'error' | 'warning' | 'validating' | 'correct';
  stateText?: string;
  stateDefaultIcon?: React.ReactNode;
}

export type MaskedInputProps = InputProps &
  React.ComponentProps<typeof IMaskInput>;

const Input = React.forwardRef<HTMLInputElement, MaskedInputProps>(
  (
    {
      type = 'text',
      disabled,
      onChange,
      // Soft limit: drives the counter only. Forwarding it to the DOM would
      // silently truncate a paste, which is the divergence it exists to surface.
      maxLength,
      hint,
      state = 'default',
      leadingIcon,
      trailingIcon,
      required,
      stateText,
      stateDefaultIcon,
      label,
      autoFocus = false,
      className,
      mask,
      ...props
    },
    ref,
  ) => {
    const [clicked, setClicked] = React.useState(false);
    const charCount = String(props.value ?? '').length;
    const iconColorRegistry = {
      default: 'var(--color_primary-dark)',
      validating: 'var(--color_system)',
      error: 'var(--color_error-dark)',
      warning: 'var(--color_warning)',
      correct: 'var(--color_success)',
      disabled: 'var(--color_disabled)',
    };
    const cleanState =
      state === 'validating' || state === 'correct' ? 'default' : state;

    const [maskProps, setMaskProps] = React.useState<
      Record<string, unknown> | undefined
    >(undefined);

    React.useEffect(() => {
      if (mask === '@currency') {
        setMaskProps({
          mask: '$num',
          blocks: {
            num: {
              mask: Number,
              scale: 0,
              min: 0,
              thousandsSeparator: ',',
            },
          },
          lazy: false,
        });
      } else if (mask) {
        setMaskProps({ mask: mask });
      } else {
        setMaskProps(undefined);
      }
    }, [mask]);

    React.useEffect(() => {
      setClicked(props.value !== undefined && props.value !== '');
    }, [props.value]);

    const inputClasses = clsx(className, {
      'with-leading-icon': leadingIcon,
      'with-trailing-icon': trailingIcon,
    });

    const labelClasses = clsx(`label-${cleanState}`, {
      [`clicked-${cleanState}`]: clicked,
      'label--trailing-icon': trailingIcon,
    });

    return (
      <div
        className={clsx('input-wrapper', `input-${cleanState}`, {
          'input-disabled': disabled,
          'input-with-hint': hint,
        })}
      >
        <div className="main-wrapper">
          {leadingIcon && <span className="leading-icon">{leadingIcon}</span>}
          <div>
            <label
              className={labelClasses}
              htmlFor={props.id || props.name}
              title={label}
            >
              {leadingIcon && (
                <span
                  className={`${
                    clicked
                      ? 'label-leading-icon_clicked'
                      : 'label-leading-icon'
                  }`}
                >
                  {leadingIcon}
                </span>
              )}
              <span className="text-label">
                {label}{' '}
                {required && (
                  <span style={{ color: 'var(--color_error)' }}>*</span>
                )}
              </span>
            </label>
            <IMaskInput
              className={inputClasses}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (mask === undefined) {
                  onChange?.(e);
                }
                e.target.value !== '' ? setClicked(true) : setClicked(false);
              }}
              type={type}
              autoFocus={autoFocus}
              disabled={disabled}
              title={props.value}
              ref={ref}
              {...props}
              {...maskProps}
              value={props.value}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                props.onBlur?.(e);
              }}
              onAccept={
                mask !== undefined
                  ? (_, maskInstance) => {
                      // For masked inputs, call onChange with the appropriate value
                      if (onChange) {
                        let value: string;
                        if (mask === '@currency') {
                          const elementValue =
                            (maskInstance.el as unknown as HTMLInputElement)
                              ?.value || '';
                          // If input is empty, pass empty string instead of "0"
                          if (!elementValue || elementValue === '$') {
                            value = '';
                          } else {
                            value = String(maskInstance.typedValue ?? '');
                          }
                        } else {
                          value = maskInstance.value;
                        }
                        const syntheticEvent = {
                          target: {
                            name: props.name,
                            value: value,
                          },
                          type: 'change',
                        } as React.ChangeEvent<HTMLInputElement>;

                        // Call props.onAccept first to allow parent components to update state
                        // before onChange is called (e.g., date-selector needs to set dateCompleteRef)
                        props.onAccept?.(_, maskInstance);

                        onChange(syntheticEvent);
                      } else {
                        props.onAccept?.(_, maskInstance);
                      }
                      if (props.name === undefined || props.name === '') return;
                      const elements = document.getElementsByName(props.name);
                      if (elements.length === 0) return;
                      elements[0].setAttribute(
                        'data-unmasked',
                        maskInstance.unmaskedValue,
                      );
                    }
                  : undefined
              }
            />
          </div>
          {trailingIcon && (
            <span className="trailing-icon">{trailingIcon}</span>
          )}
        </div>
        {/* Follows the field so it paints over the border; a z-index would also lift it over the sticky mobile header. */}
        {hint && (
          <div className="top-wrapper">
            <Hint title={label} {...hint} />
          </div>
        )}
        {stateText && (
          <div className="bottom-wrapper">
            <div className="left">
              {state === 'default' && stateDefaultIcon ? (
                stateDefaultIcon
              ) : (
                <Icon
                  type={
                    state === 'error'
                      ? 'x'
                      : state === 'warning'
                        ? 'warning'
                        : state === 'validating'
                          ? 'loading'
                          : state === 'correct'
                            ? 'success'
                            : null
                  }
                  color={
                    disabled === true
                      ? iconColorRegistry.disabled
                      : iconColorRegistry[state]
                  }
                />
              )}
              {state !== 'validating' && state !== 'correct' ? (
                <div className="state-text">{stateText}</div>
              ) : state === 'correct' ? (
                <div className="state-text_correct">Correct!</div>
              ) : (
                <div className="state-text_validating"> Validating...</div>
              )}
            </div>
            <div className="right">
              {maxLength && (
                <span
                  className={
                    charCount > maxLength
                      ? 'char-count char-count--over'
                      : 'char-count'
                  }
                >{`${charCount}/${maxLength}`}</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  },
);

export { Input };
